import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useFontSize } from "./theme/FontSizeContext";
import { useTheme as useAppTheme } from "./theme/ThemeContext";
import { useEdition } from "./bible/edition";
import VerseSelectionDock from "./components/VerseSelectionDock";
import SpeechBar from "./components/SpeechBar";
import { scrollBehavior } from "./bible/speech";
import {
  consumeContinueListen,
  loadSpeechPrefs,
  markContinueListen,
  saveSpeechPrefs,
} from "./bible/speechPrefs";
import useSpeech from "./bible/useSpeech";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import { useZenMode } from "./theme/ZenModeContext";
import useFullscreen from "./hooks/useFullscreen";
import useWakeLock from "./hooks/useWakeLock";
import {
  loadChapterHighlights,
  setRangeHighlights,
  getHighlightStyle,
} from "./bible/highlightStorage";
import { buildShareUrl, parseVerseParam } from "./bible/shareUrl";
import {
  useChapterProgress,
  ChapterTopProgressBar,
  ChapterProgressCapsule,
} from "./components/ChapterProgress";
import { saveLastReadChapter } from "./bible/readingHistoryStorage";
import { setReaderVerseFocus } from "./bible/readerVerseFocus";
import { useReaderPersistGate } from "./bible/readerPersistGate";
import { useAuth } from "./firebase/authContext";
import { formatNarrowChapterChip, getBookNameByChapterId, parseChapterId } from "./bible/bookNames";
import { emptyRange, nextVerseRange } from "./bible/verseSelection";
import { cleanVerseQuoteText } from "./bible/verseQuote";
import {
  getReaderContainerSx,
  readerChapterChipSx,
  readerPaperSx,
  revealElementAboveSelectionDock,
  SELECTION_DOCK_RESERVE_PX,
} from "./theme/readerLayout";

const parseVerses = (content) => {
  if (!content) return [];
  return String(content)
    .split(/\r?\n/)
    .map((line) => line.trim().match(/^(\d+)\s*(.*)$/))
    .filter(Boolean)
    .map((match) => ({
      number: Number(match[1]),
      text: (match[2] || "").trim(),
    }));
};

const copyToClipboard = async (value) => {
  if (!value) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

const CompareChapter = () => {
  const { chapterId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const edition = useEdition();
  const { currentUser } = useAuth();
  const canPersistReader = useReaderPersistGate(chapterId, currentUser?.uid);
  const focusVerse = searchParams.get("v");
  const { fontSize } = useFontSize();
  const { isZenMode, toggleZenMode } = useZenMode();
  const { isFullscreen, isSupported: isFullscreenSupported, toggleFullscreen } =
    useFullscreen();
  const wakeLock = useWakeLock();
  const theme = useTheme();
  const { contentWidth = 80 } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const ink = isDark ? "#e2c48a" : "#8b6914";
  const wash = isDark ? "rgba(226, 196, 138, 0.14)" : "rgba(196, 162, 101, 0.18)";
  const listenInk = isDark ? "#7ec8c8" : "#2a6f6f";
  const listenWash = isDark ? "rgba(46, 140, 140, 0.18)" : "rgba(42, 111, 111, 0.12)";
  const playedThroughEndRef = useRef(false);
  const handleListenRef = useRef(() => {});
  const [listenMode, setListenMode] = useState(
    () => loadSpeechPrefs().listenMode || "parallel"
  );
  const [loading, setLoading] = useState(true);
  const [chapter, setChapter] = useState(null);
  const [allChapters, setAllChapters] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [headings, setHeadings] = useState([]);
  const [selectedRange, setSelectedRange] = useState(emptyRange);
  const selectedRangeRef = useRef(selectedRange);
  const versesRef = useRef(null);
  const touchStateRef = useRef({ startX: 0, startY: 0, startTime: 0 });
  const [highlights, setHighlights] = useState(() => loadChapterHighlights(chapterId));

  useEffect(() => {
    setHighlights(loadChapterHighlights(chapterId));
  }, [chapterId]);

  const handleHighlight = useCallback(
    (colorId) => {
      if (!selectedRange.start) return;
      const start = selectedRange.start;
      const end = selectedRange.end || selectedRange.start;
      const updated = setRangeHighlights(chapterId, start, end, colorId);
      setHighlights(updated);
    },
    [chapterId, selectedRange.end, selectedRange.start]
  );
  const speech = useSpeech({
    onComplete: () => {
      if (!speech.autoAdvance || !playedThroughEndRef.current) return;
      if (currentIndex < 0 || currentIndex >= allChapters.length - 1) return;
      const next = allChapters[currentIndex + 1];
      if (!next) return;
      markContinueListen(next.id);
      navigate(edition.chapterPath(next.id));
    },
  });

  useEffect(() => {
    selectedRangeRef.current = selectedRange;
  }, [selectedRange]);

  useEffect(() => {
    if (!chapterId || !canPersistReader) return;
    const parsed = parseChapterId(chapterId);
    saveLastReadChapter(
      {
        chapterId,
        bookName: getBookNameByChapterId(chapterId, false),
        chapterNumber: parsed.chapterNumber || 1,
        editionId: edition.id,
      },
      currentUser?.uid
    );
  }, [chapterId, edition.id, canPersistReader, currentUser?.uid]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([edition.loadChapters(), edition.loadTitles()]).then(
      ([chapters, titlesModule]) => {
        if (cancelled) return;
        const index = chapters.findIndex((item) => item.id === chapterId);
        setAllChapters(chapters);
        setCurrentIndex(index);
        const targetChapter = chapters[index] || null;
        setChapter(targetChapter);
        if (targetChapter && canPersistReader) {
          saveLastReadChapter(
            {
              chapterId: targetChapter.id,
              bookName: getBookNameByChapterId(targetChapter.id, false),
              chapterNumber: targetChapter.chapterNumber,
              title: targetChapter.title,
              editionId: edition.id,
            },
            currentUser?.uid
          );
        }
        const section = titlesModule.chapterSectionHeadings || {};
        setHeadings(section[chapterId] || []);
        setLoading(false);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [chapterId, edition, canPersistReader, currentUser?.uid]);

  useEffect(() => {
    setSelectedRange(emptyRange);
    speech.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stop is stable
  }, [chapterId]);

  const speakingVerse = speech.current?.verse;
  useEffect(() => {
    if (!speakingVerse) return;
    versesRef.current
      ?.querySelector(`[data-verse="${speakingVerse}"]`)
      ?.scrollIntoView({ block: "center", behavior: scrollBehavior() });
  }, [speakingVerse]);

  const pairs = useMemo(() => {
    if (!chapter) return [];
    const zhMap = new Map(parseVerses(chapter.content).map((item) => [item.number, item.text]));
    const enMap = new Map(
      parseVerses(chapter.altContent).map((item) => [item.number, item.text])
    );
    const numbers = Array.from(new Set([...zhMap.keys(), ...enMap.keys()])).sort(
      (a, b) => a - b
    );
    return numbers.map((number) => ({
      number,
      zh: zhMap.get(number) || "",
      en: enMap.get(number) || "",
    }));
  }, [chapter]);

  const headingByVerse = useMemo(() => {
    if (!Array.isArray(headings)) return {};
    return headings.reduce((acc, item) => {
      if (!acc[item.verse]) acc[item.verse] = [];
      acc[item.verse].push(item.title);
      return acc;
    }, {});
  }, [headings]);

  const sectionList = useMemo(
    () =>
      (Array.isArray(headings) ? headings : [])
        .map((item) => ({
          verse: Number(item.verse),
          title: item.title,
        }))
        .filter((item) => Number.isFinite(item.verse)),
    [headings]
  );

  const verseNumbers = useMemo(() => pairs.map((p) => p.number), [pairs]);
  const verseTextMap = useMemo(() => {
    return pairs.reduce((acc, p) => {
      acc[p.number] = p.zh || p.en || "";
      return acc;
    }, {});
  }, [pairs]);

  const chapterProgress = useChapterProgress({
    chapterId,
    verseNumbers,
    verseTextMap,
    sectionList,
    lang: edition.id,
  });

  useEffect(() => {
    if (!chapterId || !canPersistReader) return;
    const routeVerse = parseVerseParam(focusVerse)?.start || null;
    setReaderVerseFocus(
      {
        chapterId,
        routeVerse,
        selectedVerse: selectedRange.start || routeVerse || undefined,
        currentVerse: chapterProgress.currentVerse || undefined,
      },
      currentUser?.uid
    );
  }, [
    chapterId,
    selectedRange.start,
    chapterProgress.currentVerse,
    focusVerse,
    canPersistReader,
    currentUser?.uid,
  ]);

  const zhBook = chapter
    ? (chapter.bookName || "").includes("-")
      ? chapter.bookName.split("-").slice(1).join("-")
      : chapter.bookName
    : "";
  const enBook = chapter?.altBookName || "";

  const bookTotalChapters = useMemo(() => {
    if (!chapter || !allChapters.length) return 0;
    const rawBook = chapter.bookName || "";
    return allChapters.filter((c) => (c.bookName || "") === rawBook).length;
  }, [chapter, allChapters]);

  const currentChapterText = useMemo(() => {
    if (!chapter) return "";
    if (edition.ui?.currentChapterLabel) {
      return edition.ui.currentChapterLabel(
        zhBook,
        enBook,
        chapter.chapterNumber,
        bookTotalChapters
      );
    }
    return `当前是：${zhBook} 第 ${chapter.chapterNumber} 章${
      bookTotalChapters ? `（共 ${bookTotalChapters} 章）` : ""
    }${enBook ? ` · ${enBook} ${chapter.chapterNumber}` : ""}`;
  }, [bookTotalChapters, chapter, edition.ui, enBook, zhBook]);

  const speechChapterTitle = useMemo(() => {
    if (!chapter) return "";
    const unit = zhBook === "诗篇" ? "篇" : "章";
    if (zhBook && chapter.chapterNumber) {
      return bookTotalChapters
        ? `${zhBook} 第${chapter.chapterNumber}/${bookTotalChapters}${unit}`
        : `${zhBook} 第${chapter.chapterNumber}${unit}`;
    }
    const plain = chapter.title ? chapter.title.replace("# ", "") : "";
    return plain.split(" · ")[0] || plain;
  }, [bookTotalChapters, chapter, zhBook]);

  const speechChapterSubtitle = useMemo(() => {
    if (!chapter) return "";
    if (enBook && chapter.chapterNumber) {
      const isPsalm = enBook.toLowerCase().includes("psalm");
      return isPsalm ? `Psalm ${chapter.chapterNumber}` : `${enBook} ${chapter.chapterNumber}`;
    }
    const plain = chapter.title ? chapter.title.replace("# ", "") : "";
    const parts = plain.split(" · ");
    return parts[1] || "";
  }, [chapter, enBook]);

  useEffect(() => {
    if (!chapter) return;
    const plainTitle = chapter.title.replace("# ", "");
    document.title = `${plainTitle} | ${edition.appTitle}`;
    return () => {
      document.title = edition.appTitle;
    };
  }, [chapter, edition.appTitle]);

  const buildReference = useCallback(
    (lang, start, end) => {
      const book = lang === "en" ? enBook : zhBook;
      const chapterNo = chapter?.chapterNumber;
      if (!book || !chapterNo || !start) return "";
      if (!end || start === end) return `${book} ${chapterNo}:${start}`;
      return `${book} ${chapterNo}:${start}-${end}`;
    },
    [chapter, enBook, zhBook]
  );

  const collectRange = useCallback(
    (start, end) => pairs.filter((item) => item.number >= start && item.number <= end),
    [pairs]
  );

  const buildFormatText = useCallback(
    (format, start, end) => {
      if (!start || !end) return "";
      const rows = collectRange(start, end);
      const shareUrl = buildShareUrl("compare", chapterId, start, end);
      if (format === "zh") {
        const body = rows
          .map((row) => `${row.number} ${cleanVerseQuoteText(row.zh)}`)
          .join("\n");
        return `${body}\n\n${buildReference("zh", start, end)}\n${shareUrl}`;
      }
      if (format === "en") {
        const body = rows
          .map((row) => `${row.number} ${cleanVerseQuoteText(row.en)}`)
          .join("\n");
        return `${body}\n\n${buildReference("en", start, end)}\n${shareUrl}`;
      }
      const body = rows
        .map(
          (row) =>
            `${row.number} ${cleanVerseQuoteText(row.zh)}\n${row.number} ${cleanVerseQuoteText(row.en)}`
        )
        .join("\n\n");
      return `${body}\n\n${buildReference("zh", start, end)}\n${buildReference("en", start, end)}\n${shareUrl}`;
    },
    [buildReference, chapterId, collectRange]
  );

  const previewByFormat = useMemo(() => {
    if (!selectedRange.start) return { parallel: "", zh: "", en: "" };
    return {
      parallel: buildFormatText("parallel", selectedRange.start, selectedRange.end),
      zh: buildFormatText("zh", selectedRange.start, selectedRange.end),
      en: buildFormatText("en", selectedRange.start, selectedRange.end),
    };
  }, [buildFormatText, selectedRange.end, selectedRange.start]);

  const neighbor = (verse, direction) => {
    const index = pairs.findIndex((item) => item.number === verse);
    if (index === -1) return null;
    return pairs[index + direction]?.number ?? null;
  };

  const playSpeech = speech.play;
  const handleListen = useCallback(
    ({ forceChapter = false, fromVerse, mode = listenMode } = {}) => {
      const lastVerse = pairs[pairs.length - 1]?.number;
      const start = fromVerse
        ? fromVerse
        : !forceChapter && selectedRange.start
          ? selectedRange.start
          : pairs[0]?.number;
      const end = fromVerse
        ? lastVerse
        : !forceChapter && selectedRange.end
          ? selectedRange.end
          : lastVerse;
      if (!start || !end) return;
      const items = [];
      collectRange(start, end).forEach((row) => {
        if (mode !== "en" && row.zh) {
          items.push({ verse: row.number, text: row.zh, lang: "zh-CN" });
        }
        if (mode !== "zh" && row.en) {
          items.push({ verse: row.number, text: row.en, lang: "en-US" });
        }
      });
      playedThroughEndRef.current = end === lastVerse;
      playSpeech(items, {
        title: chapter?.title?.replace("# ", "") || "",
        artist: edition.versionName,
      });
    },
    [
      chapter,
      collectRange,
      edition.versionName,
      listenMode,
      pairs,
      playSpeech,
      selectedRange.end,
      selectedRange.start,
    ]
  );
  handleListenRef.current = handleListen;

  const handleModeChange = (nextMode) => {
    setListenMode(nextMode);
    saveSpeechPrefs({ listenMode: nextMode });
    if (speech.status === "playing" || speech.status === "paused") {
      handleListen({
        mode: nextMode,
        forceChapter: !selectedRange.start,
      });
    }
  };

  useEffect(() => {
    if (loading || !chapter || chapter.id !== chapterId) return undefined;
    if (!consumeContinueListen(chapterId)) return undefined;
    const timer = window.setTimeout(() => {
      handleListenRef.current({ forceChapter: true });
    }, 220);
    return () => window.clearTimeout(timer);
  }, [chapter, chapterId, loading]);

  const applyRange = (start, end) => setSelectedRange({ start, end });

  const clearSelection = useCallback(() => setSelectedRange(emptyRange), []);

  const handleVerseTap = (verse) => {
    const next = nextVerseRange(selectedRangeRef.current, verse, neighbor);
    if (!next.start) {
      clearSelection();
      return;
    }
    applyRange(next.start, next.end);
  };

  useEffect(() => {
    if (!focusVerse || loading || !chapter) return undefined;
    const parsed = parseVerseParam(focusVerse);
    if (!parsed) return undefined;

    setSelectedRange({ start: parsed.start, end: parsed.end });

    const timer = window.setTimeout(() => {
      const node = versesRef.current?.querySelector(`[data-verse="${parsed.start}"]`);
      node?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [chapter, focusVerse, loading]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        clearSelection();
        return;
      }
      if (
        selectedRangeRef.current.start &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "c" &&
        !window.getSelection()?.toString()
      ) {
        event.preventDefault();
        const { start, end } = selectedRangeRef.current;
        copyToClipboard(buildFormatText("parallel", start, end));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [buildFormatText, clearSelection]);

  useEffect(() => {
    if (!selectedRange.start) return undefined;
    const timer = window.setTimeout(() => {
      const node = versesRef.current?.querySelector(
        `[data-verse="${selectedRange.start}"]`
      );
      revealElementAboveSelectionDock(node, { isMobile });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [isMobile, selectedRange.start, selectedRange.end]);

  if (loading) return <CircularProgress />;

  if (!chapter) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" color="error">
            {edition.ui.notFound}
          </Typography>
        </Paper>
      </Container>
    );
  }

  const hasSelection = Boolean(selectedRange.start);
  const selectedCount = hasSelection
    ? collectRange(selectedRange.start, selectedRange.end).length
    : 0;
  const displayTitle = chapter.title.replace("# ", "");
  const [zhTitle, enTitle] = displayTitle.split(" · ");
  const shortNavTitle = (title) => {
    const plain = (title || "").replace("# ", "");
    return plain.split(" · ")[0] || plain;
  };

  const handleTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    touchStateRef.current = {
      startX: e.touches[0].clientX,
      startY: e.touches[0].clientY,
      startTime: Date.now(),
    };
  };

  const handleTouchEnd = (e) => {
    if (e.changedTouches.length !== 1) return;
    const touch = e.changedTouches[0];
    const diffX = touch.clientX - touchStateRef.current.startX;
    const diffY = touch.clientY - touchStateRef.current.startY;
    const elapsed = Date.now() - touchStateRef.current.startTime;

    if (Math.abs(diffX) > 70 && Math.abs(diffY) < 60 && elapsed < 500) {
      if (diffX < 0 && currentIndex < allChapters.length - 1) {
        navigate(edition.chapterPath(allChapters[currentIndex + 1].id));
      } else if (diffX > 0 && currentIndex > 0) {
        navigate(edition.chapterPath(allChapters[currentIndex - 1].id));
      }
    }
  };

  return (
    <>
      <ChapterTopProgressBar
        scrollPercent={chapterProgress.scrollPercent}
        visible={chapterProgress.progressStyle !== "hidden"}
      />
      <Container
      maxWidth={false}
      sx={getReaderContainerSx(contentWidth, { hasSelection })}
    >
      <Paper
        elevation={0}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={readerPaperSx}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            mb: { xs: 2, sm: 3 },
            gap: { xs: 1, sm: 1.5 },
          }}
        >
          <IconButton
            onClick={() => navigate(edition.homePath)}
            sx={{
              p: { xs: 0.75, sm: 1 },
              bgcolor: "action.hover",
              "&:hover": { bgcolor: "action.selected" },
              flexShrink: 0,
            }}
            aria-label={edition.ui.back}
          >
            <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {currentChapterText && (
              <Box sx={{ mb: 0.75 }}>
                <Chip
                  label={
                    isMobile
                      ? formatNarrowChapterChip({
                          bookName: zhBook,
                          chapterNumber: chapter.chapterNumber,
                          total: bookTotalChapters,
                        })
                      : currentChapterText
                  }
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={readerChapterChipSx}
                />
              </Box>
            )}
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 800,
                letterSpacing: "-0.02em",
                lineHeight: 1.25,
                fontSize: { xs: "1.35rem", sm: "2.125rem" },
              }}
            >
              {zhTitle || displayTitle}
            </Typography>
            {enTitle && (
              <Typography
                variant="subtitle1"
                sx={{
                  mt: 0.4,
                  color: "text.secondary",
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontStyle: "italic",
                  fontSize: { xs: "0.95rem", sm: "1.25rem" },
                }}
              >
                {enTitle}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: "block",
                mt: 0.6,
                letterSpacing: "0.02em",
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              }}
            >
              {isMobile ? edition.ui.selectHintMobile : edition.ui.selectHintDesktop}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, sm: 0.75 },
              flexShrink: 0,
            }}
          >
            {wakeLock.isSupported && (
              <Tooltip
                title={
                  wakeLock.isEnabled
                    ? wakeLock.isActive
                      ? edition.ui.wakeLockActive || "屏幕常亮中 (点击关闭)"
                      : edition.ui.wakeLockActive || "保持常亮 (点击关闭)"
                    : edition.ui.wakeLockInactive || "屏幕常亮已关闭 (点击开启)"
                }
              >
                <IconButton
                  onClick={wakeLock.toggleEnabled}
                  aria-label={
                    wakeLock.isEnabled
                      ? edition.ui.wakeLockActive || "关闭屏幕常亮"
                      : edition.ui.wakeLockInactive || "开启屏幕常亮"
                  }
                  sx={{
                    color: wakeLock.isActive ? "warning.main" : "text.secondary",
                    bgcolor: (theme) =>
                      wakeLock.isActive
                        ? theme.palette.mode === "dark"
                          ? "rgba(255, 179, 0, 0.16)"
                          : "rgba(255, 160, 0, 0.12)"
                        : theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(0, 0, 0, 0.04)",
                    "&:hover": { bgcolor: "action.hover" },
                    p: { xs: 0.75, sm: 1 },
                  }}
                >
                  {wakeLock.isActive ? (
                    <LightbulbIcon fontSize={isMobile ? "small" : "medium"} />
                  ) : (
                    <LightbulbOutlinedIcon fontSize={isMobile ? "small" : "medium"} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {isFullscreenSupported && (
              <Tooltip
                title={
                  isFullscreen
                    ? edition.ui.exitFullscreen || "退出全屏 (Esc)"
                    : edition.ui.fullscreen || "全屏阅读 (F)"
                }
              >
                <IconButton
                  onClick={toggleFullscreen}
                  aria-label={
                    isFullscreen
                      ? edition.ui.exitFullscreen || "退出全屏"
                      : edition.ui.fullscreen || "全屏阅读"
                  }
                  sx={{
                    color: isFullscreen ? "primary.main" : "text.secondary",
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.06)"
                        : "rgba(0, 0, 0, 0.04)",
                    "&:hover": { bgcolor: "action.hover" },
                    p: { xs: 0.75, sm: 1 },
                  }}
                >
                  {isFullscreen ? (
                    <FullscreenExitIcon fontSize={isMobile ? "small" : "medium"} />
                  ) : (
                    <FullscreenIcon fontSize={isMobile ? "small" : "medium"} />
                  )}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title={
                isZenMode
                  ? edition.ui.exitZenMode || "退出专注 (Esc)"
                  : edition.ui.zenMode || "专注阅读 (Z)"
              }
            >
              <IconButton
                onClick={toggleZenMode}
                aria-label={edition.ui.zenMode || "专注模式"}
                sx={{
                  color: isZenMode ? "primary.main" : "text.secondary",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.06)"
                      : "rgba(0, 0, 0, 0.04)",
                  "&:hover": { bgcolor: "action.hover" },
                  p: { xs: 0.75, sm: 1 },
                }}
              >
                {isZenMode ? (
                  <CloseFullscreenIcon fontSize={isMobile ? "small" : "medium"} />
                ) : (
                  <SelfImprovementIcon fontSize={isMobile ? "small" : "medium"} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <SpeechBar
          supported={speech.supported}
          status={speech.status}
          current={speech.current}
          index={speech.index}
          total={speech.total}
          hasSelection={hasSelection}
          labels={edition.ui}
          chapterTitle={speechChapterTitle}
          bookChapter={Number(chapter?.chapterNumber) || 0}
          bookTotalChapters={bookTotalChapters}
          chapterSubtitle={speechChapterSubtitle}
          onChapterClick={chapterProgress.scrollToTop}
          modes={[
            { id: "parallel", label: edition.ui.modeParallel },
            { id: "zh", label: edition.ui.modeZh },
            { id: "en", label: edition.ui.modeEn },
          ]}
          mode={listenMode}
          onModeChange={handleModeChange}
          rate={speech.rate}
          onRateChange={speech.setRate}
          autoAdvance={speech.autoAdvance}
          onAutoAdvanceChange={speech.setAutoAdvance}
          sleepTimer={speech.sleepTimer}
          onSleepTimerChange={speech.setSleepTimer}
          voices={speech.voices}
          voiceURI={speech.voiceURI}
          onVoiceChange={speech.setVoiceURI}
          voiceLangs={
            listenMode === "en"
              ? ["en-US"]
              : listenMode === "zh"
                ? ["zh-CN"]
                : ["zh-CN", "en-US"]
          }
          onPlay={() => handleListen()}
          onPause={speech.pause}
          onResume={speech.resume}
          onStop={speech.stop}
          onSkip={speech.skip}
          sections={sectionList}
          onPlaySection={(verse) => handleListen({ fromVerse: verse })}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <Button
            variant="text"
            size={isMobile ? "small" : "medium"}
            startIcon={<NavigateBeforeIcon />}
            disabled={currentIndex <= 0}
            onClick={() =>
              currentIndex > 0 &&
              navigate(edition.chapterPath(allChapters[currentIndex - 1].id))
            }
            sx={{ minWidth: 0, justifyContent: "flex-start" }}
          >
            {currentIndex > 0
              ? shortNavTitle(allChapters[currentIndex - 1].title)
              : ""}
          </Button>
          <Button
            variant="text"
            size={isMobile ? "small" : "medium"}
            endIcon={<NavigateNextIcon />}
            disabled={currentIndex >= allChapters.length - 1}
            onClick={() =>
              currentIndex < allChapters.length - 1 &&
              navigate(edition.chapterPath(allChapters[currentIndex + 1].id))
            }
            sx={{ minWidth: 0, justifyContent: "flex-end" }}
          >
            {currentIndex < allChapters.length - 1
              ? shortNavTitle(allChapters[currentIndex + 1].title)
              : ""}
          </Button>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <Box ref={versesRef}>
          {pairs.map((row, index) => {
            const speaking = speech.current?.verse === row.number;
            const speakingZh =
              speaking && String(speech.current?.lang || "").startsWith("zh");
            const speakingEn =
              speaking && String(speech.current?.lang || "").startsWith("en");
            const selected =
              hasSelection &&
              row.number >= selectedRange.start &&
              row.number <= selectedRange.end;
            const isStart = selected && row.number === selectedRange.start;
            const isEnd = selected && row.number === selectedRange.end;
            const nextSelected =
              selected && pairs[index + 1] && pairs[index + 1].number <= selectedRange.end;
            const titles = headingByVerse[row.number] || [];
            const hlColor = highlights[String(row.number)];
            const hlStyle = hlColor ? getHighlightStyle(hlColor, isDark) : null;

            return (
              <Box key={row.number}>
                {titles.map((title) => (
                  <Box
                    key={title}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      mt: { xs: 2.5, sm: 3.5 },
                      mb: 1.25,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        flex: 1,
                        fontWeight: "bold",
                        color: "primary.dark",
                        borderLeft: "4px solid",
                        borderColor: "primary.main",
                        pl: 1.5,
                        py: 0.5,
                        fontSize: { xs: "1.05rem", sm: "1.25rem" },
                      }}
                    >
                      {title}
                    </Typography>
                    <Tooltip title={edition.ui.listenFromHere}>
                      <IconButton
                        size="small"
                        aria-label={edition.ui.listenFromHere}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleListen({ fromVerse: row.number });
                        }}
                      >
                        <PlayArrowIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
                <Box
                  data-verse={row.number}
                  aria-selected={selected}
                  onClick={() => handleVerseTap(row.number)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "28px 1fr", sm: "40px 1fr" },
                    columnGap: { xs: 1, sm: 1.5 },
                    scrollMarginBottom: {
                      xs: `${SELECTION_DOCK_RESERVE_PX.xs}px`,
                      sm: `${SELECTION_DOCK_RESERVE_PX.sm}px`,
                    },
                    px: { xs: 0.75, sm: 1.25 },
                    py: { xs: 1.25, sm: 1.4 },
                    mb: selected && nextSelected ? 0 : { xs: 0.75, sm: 1 },
                    cursor: "pointer",
                    bgcolor: speaking
                      ? listenWash
                      : selected
                        ? hlStyle
                          ? hlStyle.backgroundColor
                          : wash
                        : hlStyle
                          ? hlStyle.backgroundColor
                          : "transparent",
                    boxShadow: speaking
                      ? `inset 3px 0 0 ${listenInk}`
                      : selected
                        ? hlStyle
                          ? `inset 4px 0 0 ${hlStyle.borderLeft ? hlStyle.borderLeft.split(" ")[2] : ink}`
                          : `inset 3px 0 0 ${ink}`
                        : hlStyle
                          ? hlStyle.borderLeft
                            ? `inset 3px 0 0 ${hlStyle.borderLeft.split(" ")[2]}`
                            : "none"
                          : "none",
                    borderRadius: selected
                      ? `${isStart ? 12 : 4}px ${isStart ? 12 : 4}px ${
                          isEnd || !nextSelected ? 12 : 4
                        }px ${isEnd || !nextSelected ? 12 : 4}px`
                      : 1,
                    userSelect: isMobile ? "none" : "text",
                    WebkitUserSelect: isMobile ? "none" : "text",
                    touchAction: "manipulation",
                    "&:hover": {
                      bgcolor: speaking
                        ? listenWash
                        : selected
                          ? wash
                          : hlStyle
                            ? hlStyle.backgroundColor
                            : "action.hover",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: speaking
                        ? listenInk
                        : selected
                          ? ink
                          : "primary.main",
                      fontWeight: 700,
                      fontSize: `${Math.max(fontSize - 1, 14)}px`,
                      userSelect: "none",
                      lineHeight: 1.75,
                    }}
                  >
                    {row.number}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: `${fontSize}px`,
                        fontWeight: 500,
                        lineHeight: 1.85,
                        color: "text.primary",
                        borderRadius: 1,
                        px: speakingZh ? 0.5 : 0,
                        bgcolor: speakingZh
                          ? "rgba(42, 111, 111, 0.12)"
                          : "transparent",
                      }}
                    >
                      {row.zh}
                    </Typography>
                    <Typography
                      sx={{
                        mt: { xs: 0.6, sm: 0.8 },
                        fontSize: `${Math.max(fontSize - (isMobile ? 1.5 : 0.5), 13.5)}px`,
                        lineHeight: 1.65,
                        color: "text.secondary",
                        fontStyle: "italic",
                        letterSpacing: "0.01em",
                        fontFamily:
                          'Georgia, "Times New Roman", "Noto Serif", serif',
                        opacity: 0.9,
                        borderRadius: 1,
                        px: speakingEn ? 0.5 : 0,
                        bgcolor: speakingEn
                          ? "rgba(42, 111, 111, 0.12)"
                          : "transparent",
                      }}
                    >
                      {row.en}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<NavigateBeforeIcon />}
            disabled={currentIndex <= 0}
            fullWidth={isMobile}
            onClick={() =>
              currentIndex > 0 &&
              navigate(edition.chapterPath(allChapters[currentIndex - 1].id))
            }
          >
            {edition.ui.prevChapter}
          </Button>
          <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
            <InputLabel>{edition.ui.jumpTo}</InputLabel>
            <Select
              value={chapter.id}
              label={edition.ui.jumpTo}
              onChange={(event) => navigate(edition.chapterPath(event.target.value))}
            >
              {allChapters.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {shortNavTitle(item.title)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            endIcon={<NavigateNextIcon />}
            disabled={currentIndex >= allChapters.length - 1}
            fullWidth={isMobile}
            onClick={() =>
              currentIndex < allChapters.length - 1 &&
              navigate(edition.chapterPath(allChapters[currentIndex + 1].id))
            }
          >
            {edition.ui.nextChapter}
          </Button>
        </Box>
      </Paper>

      <VerseSelectionDock
        open={hasSelection}
        reference={`${buildReference("zh", selectedRange.start, selectedRange.end)} · ${buildReference("en", selectedRange.start, selectedRange.end)}`}
        verseCount={selectedCount}
        previewByFormat={previewByFormat}
        canExpandStart={Boolean(selectedRange.start && neighbor(selectedRange.start, -1))}
        canExpandEnd={Boolean(selectedRange.end && neighbor(selectedRange.end, 1))}
        onExpandStart={() => {
          const prev = neighbor(selectedRange.start, -1);
          if (prev) applyRange(prev, selectedRange.end);
        }}
        onExpandEnd={() => {
          const next = neighbor(selectedRange.end, 1);
          if (next) applyRange(selectedRange.start, next);
        }}
        onCopy={(format) =>
          copyToClipboard(
            buildFormatText(format, selectedRange.start, selectedRange.end)
          )
        }
        onShare={(format) => {
          const payload = buildFormatText(
            format,
            selectedRange.start,
            selectedRange.end
          );
          if (!payload) return;
          window.open(
            `https://wa.me/?text=${encodeURIComponent(payload)}`,
            "_blank",
            "noopener,noreferrer"
          );
        }}
        onPlay={() => {
          if (selectedCount === 1) {
            handleListen({ fromVerse: selectedRange.start });
          } else {
            handleListen();
          }
        }}
        onClear={clearSelection}
        onHighlight={handleHighlight}
        activeHighlightColor={selectedRange.start ? highlights[String(selectedRange.start)] : null}
        isMobile={isMobile}
        copyUi={edition.copy}
      />

      {isZenMode && (
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 1280,
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 0.75,
            borderRadius: 999,
            bgcolor: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(18, 24, 24, 0.9)"
                : "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(12px)",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 6px 24px rgba(0,0,0,0.15)",
          }}
        >
          <Tooltip title={edition.ui.prevChapter} placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (currentIndex > 0) {
                    navigate(edition.chapterPath(allChapters[currentIndex - 1].id));
                  }
                }}
                disabled={currentIndex <= 0}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            size="small"
            variant="text"
            startIcon={<CloseFullscreenIcon sx={{ fontSize: 14 }} />}
            onClick={toggleZenMode}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              borderRadius: 999,
              px: 1.2,
              color: "text.primary",
            }}
          >
            {edition.ui.exitZenMode || "退出专注 (Esc)"}
          </Button>

          {wakeLock.isSupported && (
            <Tooltip
              title={
                wakeLock.isEnabled
                  ? wakeLock.isActive
                    ? edition.ui.wakeLockActive || "屏幕常亮中 (点击关闭)"
                    : edition.ui.wakeLockActive || "保持常亮 (点击关闭)"
                  : edition.ui.wakeLockInactive || "屏幕常亮已关闭 (点击开启)"
              }
              placement="top"
            >
              <IconButton
                size="small"
                onClick={wakeLock.toggleEnabled}
                aria-label={
                  wakeLock.isEnabled
                    ? edition.ui.wakeLockActive || "关闭屏幕常亮"
                    : edition.ui.wakeLockInactive || "开启屏幕常亮"
                }
                sx={{
                  color: wakeLock.isActive ? "warning.main" : "text.secondary",
                  p: 0.5,
                }}
              >
                {wakeLock.isActive ? (
                  <LightbulbIcon sx={{ fontSize: 16 }} />
                ) : (
                  <LightbulbOutlinedIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={edition.ui.nextChapter} placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  if (currentIndex < allChapters.length - 1) {
                    navigate(edition.chapterPath(allChapters[currentIndex + 1].id));
                  }
                }}
                disabled={currentIndex >= allChapters.length - 1}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
    </Container>

    <ChapterProgressCapsule
      chapterTitle={
        zhBook && enBook
          ? `${zhBook} ${chapter?.chapterNumber} · ${enBook} ${chapter?.chapterNumber}`
          : chapter?.title?.replace("# ", "") || ""
      }
      currentVerse={chapterProgress.currentVerse}
      totalVerses={chapterProgress.totalVerses}
      firstVerse={chapterProgress.firstVerse}
      lastVerse={chapterProgress.lastVerse}
      scrollPercent={chapterProgress.scrollPercent}
      isScrolling={chapterProgress.isScrolling}
      scrollDirection={chapterProgress.scrollDirection}
      isCompleted={chapterProgress.isCompleted}
      progressStyle={chapterProgress.progressStyle}
      estimatedMinutesRemaining={chapterProgress.estimatedMinutesRemaining}
      currentSection={chapterProgress.currentSection}
      sectionList={sectionList}
      verseTextMap={verseTextMap}
      hasSelection={hasSelection}
      isZenMode={isZenMode}
      toggleCompleted={chapterProgress.toggleCompleted}
      scrollToVerse={chapterProgress.scrollToVerse}
      scrollToTop={chapterProgress.scrollToTop}
      setStyle={chapterProgress.setStyle}
      labels={edition.ui}
    />
  </>
);
};

export default CompareChapter;
